/**
 * Kasa Credentials Setup Card
 *
 * Collects TP-Link account credentials for KLAP device authentication.
 * Older Kasa devices work without credentials, but newer ones require them.
 */

import React, { useState } from 'react';
import { useKasaStore } from '../kasa-store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Plug, ExternalLink } from 'lucide-react';

export const CredentialsSetupCard: React.FC = () => {
  const { saveCredentials, discoverDevices, isLoading } = useKasaStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSave = async () => {
    if (email && password) {
      await saveCredentials(email, password);
    }
  };

  const handleDiscoverOnly = async () => {
    await discoverDevices();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Plug className="h-5 w-5" />
          Connect TP-Link Kasa/Tapo
        </CardTitle>
        <CardDescription>
          Enter your TP-Link account credentials to control newer Kasa and Tapo devices.
          Older Kasa devices can be discovered without credentials.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">TP-Link Email</label>
            <Input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Password</label>
            <Input
              type="password"
              placeholder="TP-Link account password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={handleSave} disabled={isLoading || !email || !password}>
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Save & Discover
          </Button>
          <Button variant="outline" onClick={handleDiscoverOnly} disabled={isLoading}>
            Discover Without Credentials
          </Button>
        </div>

        <div className="text-xs text-muted-foreground space-y-1">
          <p className="flex items-center gap-1">
            <ExternalLink className="h-3 w-3" />
            <span>Credentials are the same as your Kasa or Tapo mobile app login.</span>
          </p>
          <p>
            Newer devices (2020+) require credentials for local control.
            Your credentials are encrypted and stored securely.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
